import { LitElement, html, css } from 'lit';
import { getConfig } from '../config.js';

/**
 * Fonctions utilitaires internes pour les dates
 */
function parseISODate(str) {
  if (!str) return null;
  if (typeof str === 'string' && str.trim().toLowerCase() === 'today') {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }
  const parts = str.split('-');
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    const date = new Date(y, m, d);
    return isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function toISODateString(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDate(date, formatPattern) {
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  const frenchMonths = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
  ];
  const monthName = frenchMonths[date.getMonth()];

  let formatted = formatPattern || 'DD/MM/YYYY';
  if (formatted.includes('MMMM')) {
    formatted = formatted.replace('MMMM', monthName);
  } else if (formatted.includes('MM')) {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    formatted = formatted.replace('MM', m);
  }

  return formatted.replace('YYYY', y).replace('DD', d);
}

function isSameDate(d1, d2) {
  if (!d1 || !d2) return false;
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
}

function parseFormattedDate(str, formatPattern) {
  if (!str) return null;
  const cleanStr = str.trim().toLowerCase().replace(/\s+/g, ' ');
  
  if (cleanStr === 'today') {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }

  // 1. Check French/English literal month format, e.g. "10 mars 2026", "10 février 2026", "10 march 2026"
  const frenchMonths = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const frenchMonthsShort = ['janv', 'févr', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];
  const englishMonths = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const englishMonthsShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  // Match: "10 mars 2026" or "10 janv. 2026"
  const litRegex = /^(\d{1,2}) ([a-zûéûôóíáäëïöüÿç\.]+)\.? (\d{4})$/;
  const matchLit = cleanStr.match(litRegex);
  if (matchLit) {
    const day = parseInt(matchLit[1], 10);
    const monthName = matchLit[2].replace('.', '');
    const year = parseInt(matchLit[3], 10);
    
    let month = frenchMonths.findIndex(m => m.startsWith(monthName) || monthName.startsWith(m));
    if (month === -1) {
      month = frenchMonthsShort.findIndex(m => m.startsWith(monthName) || monthName.startsWith(m));
    }
    if (month === -1) {
      month = englishMonths.findIndex(m => m.startsWith(monthName) || monthName.startsWith(m));
    }
    if (month === -1) {
      month = englishMonthsShort.findIndex(m => m.startsWith(monthName) || monthName.startsWith(m));
    }
    
    if (month !== -1) {
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime()) && date.getMonth() === month && date.getDate() === day) {
        return date;
      }
    }
  }

  // 2. Match standard numeric formats: "10/02/2026", "10-02-2026", "2026-02-10", "10.02.2026"
  const numRegex = /^(\d{1,4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,4})$/;
  const matchNum = cleanStr.match(numRegex);
  if (matchNum) {
    const part1 = parseInt(matchNum[1], 10);
    const part2 = parseInt(matchNum[2], 10);
    const part3 = parseInt(matchNum[3], 10);
    
    let day, month, year;
    if (part1 > 1000) { // YYYY/MM/DD
      year = part1;
      month = part2 - 1;
      day = part3;
    } else { // DD/MM/YYYY or MM/DD/YYYY (defaulting to European DD/MM/YYYY)
      day = part1;
      month = part2 - 1;
      year = part3;
      if (year < 100) {
        year += 2000;
      }
    }
    
    const date = new Date(year, month, day);
    if (!isNaN(date.getTime()) && date.getMonth() === month && date.getDate() === day) {
      return date;
    }
  }

  // 3. Fallback to format pattern based parsing if specified
  if (formatPattern) {
    const formatUpper = formatPattern.toUpperCase();
    let dayIdx = formatUpper.indexOf('DD');
    let monthIdx = formatUpper.indexOf('MM');
    let yearIdx = formatUpper.indexOf('YYYY');
    
    if (dayIdx !== -1 && monthIdx !== -1 && yearIdx !== -1) {
      let day, month, year;
      try {
        if (yearIdx === 0) { // YYYY-MM-DD
          year = parseInt(str.substring(0, 4), 10);
          month = parseInt(str.substring(5, 7), 10) - 1;
          day = parseInt(str.substring(8, 10), 10);
        } else if (dayIdx === 0) { // DD/MM/YYYY
          day = parseInt(str.substring(0, 2), 10);
          month = parseInt(str.substring(3, 5), 10) - 1;
          year = parseInt(str.substring(6, 10), 10);
        }
        
        if (year && month !== undefined && day) {
          const date = new Date(year, month, day);
          if (!isNaN(date.getTime()) && date.getMonth() === month && date.getDate() === day) {
            return date;
          }
        }
      } catch (e) {
        // Fall through
      }
    }
  }

  // Final fallback: native JavaScript Date parsing
  const nativeDate = new Date(str);
  return isNaN(nativeDate.getTime()) ? null : nativeDate;
}

/**
 * Composant <ui-datepicker>
 * Calendrier et sélecteur de date interactif moderne sans aucun menu natif
 */
export class UIDatePicker extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Format ISO YYYY-MM-DD
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    min: { type: String }, // Format ISO
    max: { type: String }, // Format ISO
    format: { type: String }, // Custom format string, e.g. "DD MMMM YYYY"
    label: { type: String, reflect: true },
    
    // États internes
    isOpen: { type: Boolean, state: true },
    viewMode: { type: String, state: true }, // 'days', 'months', 'years'
    currentMonth: { type: Number, state: true },
    currentYear: { type: Number, state: true },
    inputValue: { type: String, state: true },
    focusedDate: { type: Object, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-weight: 600;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .datepicker-container {
      position: relative;
      width: 100%;
    }

    /* Le Header (Input principal) */
    .datepicker-header {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
    }

    .datepicker-input {
      width: 100%;
      height: var(--ui-input-height);
      padding: 0 var(--ui-padding-x);
      padding-right: 40px;
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .datepicker-input:hover:not(:disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .datepicker-input:focus:not(:disabled) {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .datepicker-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    .datepicker-icon {
      position: absolute;
      right: var(--ui-padding-x);
      top: 50%;
      transform: translateY(-50%);
      color: var(--ui-text-muted);
      pointer-events: none;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--ui-transition-fast);
    }

    .datepicker-container.open .datepicker-icon {
      color: var(--ui-primary);
    }

    /* Le Calendrier Popup */
    .datepicker-calendar {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 300px;
      padding: 16px;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-popover);
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
      box-sizing: border-box;
    }

    .datepicker-calendar.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* Navigation En-tête */
    .calendar-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .calendar-title {
      display: flex;
      gap: 4px;
      font-weight: 600;
      color: var(--ui-text);
      font-size: var(--ui-font-size-base);
    }

    /* Boutons de sélection d'en-tête (cliquables) */
    .header-btn {
      border: none;
      background: transparent;
      color: var(--ui-text);
      font-family: inherit;
      font-size: var(--ui-font-size-base);
      font-weight: 600;
      cursor: pointer;
      outline: none;
      padding: 4px 8px;
      border-radius: var(--ui-radius-sm);
      transition: var(--ui-transition-fast);
      text-transform: capitalize;
    }

    .header-btn:hover {
      background-color: var(--ui-surface-hover);
      color: var(--ui-primary);
    }

    .calendar-nav-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      background-color: transparent;
      color: var(--ui-text);
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .calendar-nav-btn:hover {
      background-color: var(--ui-surface-hover);
      border-color: var(--ui-border-hover);
      color: var(--ui-primary);
    }

    /* VUE JOURS */
    .calendar-weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      text-align: center;
      margin-bottom: 8px;
      font-weight: 600;
      color: var(--ui-text-muted);
      font-size: var(--ui-font-size-sm);
    }

    .calendar-days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
    }

    .calendar-day {
      display: flex;
      align-items: center;
      justify-content: center;
      aspect-ratio: 1;
      border-radius: var(--ui-radius-sm);
      font-size: var(--ui-font-size-base);
      cursor: pointer;
      transition: var(--ui-transition-fast);
      user-select: none;
    }

    .calendar-day:hover:not(.disabled):not(.selected) {
      background-color: var(--ui-surface-hover);
      color: var(--ui-text);
    }

    .calendar-day:focus {
      outline: 2px solid var(--ui-primary);
      outline-offset: -2px;
    }

    .calendar-day.selected {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      font-weight: 600;
    }

    .calendar-day.today:not(.selected) {
      border: 1.5px solid var(--ui-primary);
      color: var(--ui-primary);
      font-weight: 600;
    }

    .calendar-day.other-month {
      color: var(--ui-text-light);
    }

    .calendar-day.disabled {
      cursor: not-allowed;
      opacity: 0.25;
      background-color: transparent;
    }

    /* VUES GRILLES MOIS ET ANNÉES */
    .grid-view-3x4 {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
      margin-top: 4px;
    }

    .grid-cell {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 44px;
      border-radius: var(--ui-radius-sm);
      cursor: pointer;
      font-size: var(--ui-font-size-base);
      font-weight: 500;
      transition: var(--ui-transition-fast);
      text-transform: capitalize;
    }

    .grid-cell:hover:not(.disabled):not(.selected) {
      background-color: var(--ui-surface-hover);
      color: var(--ui-primary);
    }

    .grid-cell.selected {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      font-weight: 600;
    }

    .grid-cell.current:not(.selected) {
      border: 1.5px solid var(--ui-primary);
      color: var(--ui-primary);
      font-weight: 600;
    }

    .grid-cell.disabled {
      cursor: not-allowed;
      opacity: 0.25;
      background-color: transparent;
    }

    /* FOOTER */
    .datepicker-footer {
      margin-top: 12px;
      padding-top: 8px;
      border-top: 1px solid var(--ui-border);
      display: flex;
      justify-content: center;
    }

    .btn-today {
      width: 100%;
      height: 32px;
      border: 1px solid var(--ui-primary);
      border-radius: var(--ui-radius-sm);
      background-color: transparent;
      color: var(--ui-primary);
      font-size: var(--ui-font-size-sm);
      font-weight: 600;
      cursor: pointer;
      transition: var(--ui-transition-fast);
    }

    .btn-today:hover {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
    }

    /* Backdrop pour version mobile */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-popover) - 1);
    }

    /* Style Responsive Mobile - Bottom Sheet */
    @media (max-width: 576px) {
      .datepicker-container.open .backdrop {
        display: block;
      }

      .datepicker-calendar {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        box-shadow: 0 -10px 25px rgba(0,0,0,0.15);
        transform: translateY(100%) scale(1);
        transform-origin: bottom;
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-popover);
      }

      .datepicker-container.open .datepicker-calendar {
        transform: translateY(0) scale(1);
        opacity: 1;
      }

      .datepicker-calendar::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: -8px auto 16px auto;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = '';
    this.disabled = false;
    this.required = false;
    this.min = '';
    this.max = '';
    this.format = '';
    this.label = '';

    // États initiaux
    this.isOpen = false;
    this.viewMode = 'days'; // 'days', 'months', 'years'
    this.inputValue = '';
    this.focusedDate = new Date();
    
    const today = new Date();
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez sélectionner une date.', this.shadowRoot.querySelector('.datepicker-input'));
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('value')) {
      const parsed = parseISODate(this.value);
      const pattern = this.format || getConfig('datepicker', 'format');
      if (parsed) {
        this.inputValue = formatDate(parsed, pattern);
        this.currentMonth = parsed.getMonth();
        this.currentYear = parsed.getFullYear();
      } else {
        this.inputValue = '';
      }
    }
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closeCalendar();
    }
  }

  _toggleCalendar() {
    if (this.disabled) return;
    if (this.isOpen) {
      this._closeCalendar();
    } else {
      this._openCalendar();
    }
  }

  _openCalendar() {
    this.isOpen = true;
    this.viewMode = 'days';
    const parsed = parseISODate(this.value);
    if (parsed) {
      this.currentMonth = parsed.getMonth();
      this.currentYear = parsed.getFullYear();
      this.focusedDate = parsed;
    } else {
      const today = new Date();
      this.currentMonth = today.getMonth();
      this.currentYear = today.getFullYear();
      this.focusedDate = today;
    }
  }

  _closeCalendar() {
    this.isOpen = false;
  }

  _handleInput(e) {
    this.inputValue = e.target.value;
    const pattern = this.format || getConfig('datepicker', 'format');
    const parsedDate = parseFormattedDate(this.inputValue, pattern);

    if (parsedDate) {
      if (this._isDateDisabled(parsedDate)) {
        return;
      }
      
      const newISO = toISODateString(parsedDate);
      if (newISO !== this.value) {
        this.value = newISO;
        this.currentMonth = parsedDate.getMonth();
        this.currentYear = parsedDate.getFullYear();
        this._dispatchChangeEvent(newISO);
      }
    }
  }

  _handleBlur() {
    const parsed = parseISODate(this.value);
    const pattern = this.format || getConfig('datepicker', 'format');
    if (parsed) {
      this.inputValue = formatDate(parsed, pattern);
    } else {
      this.inputValue = '';
    }
  }

  _selectDay(dayCell) {
    if (dayCell.disabled) return;
    
    const newDate = new Date(dayCell.year, dayCell.month, dayCell.day);
    const newISO = toISODateString(newDate);
    
    this.value = newISO;
    this._closeCalendar();
    this._dispatchChangeEvent(newISO);
  }

  _selectMonth(monthIndex) {
    this.currentMonth = monthIndex;
    this.viewMode = 'days';
  }

  _selectYear(year) {
    this.currentYear = year;
    this.viewMode = 'months'; // renvoie au mois après sélection de l'année
  }

  _selectToday() {
    const today = new Date();
    const todayISO = toISODateString(today);
    
    if (this._isDateDisabled(today)) {
      return;
    }
    
    this.value = todayISO;
    this.currentMonth = today.getMonth();
    this.currentYear = today.getFullYear();
    this.viewMode = 'days';
    this._closeCalendar();
    this._dispatchChangeEvent(todayISO);
  }

  _dispatchChangeEvent(isoValue) {
    this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    const formatted = formatDate(parseISODate(isoValue), this.format || getConfig('datepicker', 'format'));
    this.dispatchEvent(new CustomEvent('ui-change', {
      detail: { value: isoValue, formatted: formatted },
      bubbles: true,
      composed: true
    }));
  }

  _isDateDisabled(date) {
    if (this.min) {
      const minDate = parseISODate(this.min);
      if (minDate && date < minDate) return true;
    }
    if (this.max) {
      const maxDate = parseISODate(this.max);
      if (maxDate && date > maxDate) return true;
    }
    return false;
  }

  _isMonthDisabled(monthIndex) {
    // Vérification rapide mois/année min/max
    if (this.min) {
      const minDate = parseISODate(this.min);
      if (minDate && this.currentYear < minDate.getFullYear()) return true;
      if (minDate && this.currentYear === minDate.getFullYear() && monthIndex < minDate.getMonth()) return true;
    }
    if (this.max) {
      const maxDate = parseISODate(this.max);
      if (maxDate && this.currentYear > maxDate.getFullYear()) return true;
      if (maxDate && this.currentYear === maxDate.getFullYear() && monthIndex > maxDate.getMonth()) return true;
    }
    return false;
  }

  _isYearDisabled(year) {
    if (this.min) {
      const minDate = parseISODate(this.min);
      if (minDate && year < minDate.getFullYear()) return true;
    }
    if (this.max) {
      const maxDate = parseISODate(this.max);
      if (maxDate && year > maxDate.getFullYear()) return true;
    }
    return false;
  }

  _prevHeaderAction() {
    if (this.viewMode === 'days') {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear -= 1;
      } else {
        this.currentMonth -= 1;
      }
    } else if (this.viewMode === 'months') {
      this.currentYear -= 1;
    } else if (this.viewMode === 'years') {
      this.currentYear -= 12; // recule d'une décennie
    }
  }

  _nextHeaderAction() {
    if (this.viewMode === 'days') {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear += 1;
      } else {
        this.currentMonth += 1;
      }
    } else if (this.viewMode === 'months') {
      this.currentYear += 1;
    } else if (this.viewMode === 'years') {
      this.currentYear += 12; // avance d'une décennie
    }
  }

  _generateCalendarCells() {
    const year = this.currentYear;
    const month = this.currentMonth;
    
    const firstDayInstance = new Date(year, month, 1);
    let firstDayIndex = firstDayInstance.getDay();
    
    const firstDayOfWeekSetting = getConfig('datepicker', 'firstDayOfWeek');
    
    let offset = firstDayIndex - firstDayOfWeekSetting;
    if (offset < 0) offset += 7;

    const cells = [];
    
    const prevMonthYear = month === 0 ? year - 1 : year;
    const prevMonth = month === 0 ? 11 : month - 1;
    const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();
    
    for (let i = offset - 1; i >= 0; i--) {
      const dayVal = daysInPrevMonth - i;
      const dateInst = new Date(prevMonthYear, prevMonth, dayVal);
      cells.push({
        day: dayVal,
        month: prevMonth,
        year: prevMonthYear,
        isOtherMonth: true,
        disabled: this._isDateDisabled(dateInst)
      });
    }

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const parsedValue = parseISODate(this.value);

    for (let i = 1; i <= daysInMonth; i++) {
      const dateInst = new Date(year, month, i);
      const isSelected = parsedValue && 
                         parsedValue.getDate() === i && 
                         parsedValue.getMonth() === month && 
                         parsedValue.getFullYear() === year;
                         
      const isToday = today.getDate() === i && 
                      today.getMonth() === month && 
                      today.getFullYear() === year;

      cells.push({
        day: i,
        month: month,
        year: year,
        isOtherMonth: false,
        selected: isSelected,
        isToday: isToday,
        disabled: this._isDateDisabled(dateInst)
      });
    }

    const nextMonthYear = month === 11 ? year + 1 : year;
    const nextMonth = month === 11 ? 0 : month + 1;
    const remainingCellsCount = 42 - cells.length;
    
    for (let i = 1; i <= remainingCellsCount; i++) {
      const dateInst = new Date(nextMonthYear, nextMonth, i);
      cells.push({
        day: i,
        month: nextMonth,
        year: nextMonthYear,
        isOtherMonth: true,
        disabled: this._isDateDisabled(dateInst)
      });
    }

    return cells;
  }

  _handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this._closeCalendar();
      this.shadowRoot.querySelector('.datepicker-input').focus();
    } else if (e.key === 'ArrowDown' && this.isOpen && this.viewMode === 'days') {
      e.preventDefault();
      const activeCell = this.shadowRoot.querySelector('.calendar-day[tabindex="0"]');
      if (activeCell) activeCell.focus();
    }
  }

  _handleGridKeyDown(e) {
    if (this.viewMode !== 'days') return;
    
    let newDate = new Date(this.focusedDate);
    let handled = false;
    
    switch (e.key) {
      case 'ArrowLeft':
        newDate.setDate(newDate.getDate() - 1);
        handled = true;
        break;
      case 'ArrowRight':
        newDate.setDate(newDate.getDate() + 1);
        handled = true;
        break;
      case 'ArrowUp':
        newDate.setDate(newDate.getDate() - 7);
        handled = true;
        break;
      case 'ArrowDown':
        newDate.setDate(newDate.getDate() + 7);
        handled = true;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        const cell = {
          year: this.focusedDate.getFullYear(),
          month: this.focusedDate.getMonth(),
          day: this.focusedDate.getDate(),
          disabled: this._isDateDisabled(this.focusedDate)
        };
        this._selectDay(cell);
        this.shadowRoot.querySelector('.datepicker-input').focus();
        handled = true;
        break;
      case 'Escape':
        e.preventDefault();
        this._closeCalendar();
        this.shadowRoot.querySelector('.datepicker-input').focus();
        handled = true;
        break;
    }
    
    if (handled) {
      e.preventDefault();
      if (this._isDateDisabled(newDate)) {
        return;
      }
      
      this.focusedDate = newDate;
      this.currentMonth = newDate.getMonth();
      this.currentYear = newDate.getFullYear();
      
      setTimeout(() => {
        const activeCell = this.shadowRoot.querySelector('.calendar-day[tabindex="0"]');
        if (activeCell) activeCell.focus();
      }, 0);
    }
  }

  render() {
    const cells = this._generateCalendarCells();
    const parsedValue = parseISODate(this.value);
    const monthsFull = getConfig('datepicker', 'months');
    const monthsShort = getConfig('datepicker', 'monthsShort');
    const monthName = monthsFull[this.currentMonth];
    const weekdayHeaders = getConfig('datepicker', 'weekdaysMin');
    const firstDaySetting = getConfig('datepicker', 'firstDayOfWeek');

    const displayedWeekdays = [...weekdayHeaders];
    if (firstDaySetting === 1) {
      const sun = displayedWeekdays.shift();
      displayedWeekdays.push(sun);
    }

    const displayPlaceholder = this.placeholder || getConfig('datepicker', 'placeholder');

    // Années pour la vue grille (12 ans centrés autour de currentYear)
    const yearsGridRange = Array.from({ length: 12 }, (_, i) => this.currentYear - 5 + i);

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="datepicker-container ui-component ${this.isOpen ? 'open' : ''}">
        <!-- Soumission automatique gérée via ElementInternals -->

        <!-- Backdrop mobile -->
        <div class="backdrop" @click="${this._closeCalendar}"></div>

        <!-- En-tête avec input -->
        <div class="datepicker-header">
          <input 
            type="text" 
            inputmode="none"
            class="datepicker-input" part="input" 
            placeholder="${displayPlaceholder}"
            .value="${this.inputValue}"
            ?disabled="${this.disabled}"
            @focus="${this._openCalendar}"
            @input="${this._handleInput}"
            @blur="${this._handleBlur}"
            @keydown="${this._handleKeyDown}"
          >
          <span class="datepicker-icon" @click="${this._toggleCalendar}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px;">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </span>
        </div>

        <!-- Calendrier Popup -->
        <div class="datepicker-calendar ${this.isOpen ? 'open' : ''}">
          <div class="calendar-header">
            <button type="button" class="calendar-nav-btn" @click="${this._prevHeaderAction}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 14px; height: 14px;">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            
            <div class="calendar-title">
              ${this.viewMode === 'days' ? html`
                <button type="button" class="header-btn" @click="${() => this.viewMode = 'months'}">${monthName}</button>
                <button type="button" class="header-btn" @click="${() => this.viewMode = 'years'}">${this.currentYear}</button>
              ` : this.viewMode === 'months' ? html`
                <span class="header-btn" style="cursor: default;">Sélectionner Mois</span>
                <button type="button" class="header-btn" @click="${() => this.viewMode = 'years'}">${this.currentYear}</button>
              ` : html`
                <span class="header-btn" style="cursor: default;">Sélectionner Année</span>
                <button type="button" class="header-btn" @click="${() => this.viewMode = 'days'}">${this.currentYear - 5} - ${this.currentYear + 6}</button>
              `}
            </div>
            
            <button type="button" class="calendar-nav-btn" @click="${this._nextHeaderAction}">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 14px; height: 14px;">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </div>

          <!-- Rendu en fonction du viewMode -->
          ${this.viewMode === 'days' ? html`
            <div class="calendar-weekdays">
              ${displayedWeekdays.map(day => html`<span>${day}</span>`)}
            </div>

            <div class="calendar-days">
              ${cells.map(cell => {
                const cellDate = new Date(cell.year, cell.month, cell.day);
                const isFocused = this.focusedDate && isSameDate(cellDate, this.focusedDate);
                
                return html`
                  <div 
                    class="calendar-day 
                      ${cell.isOtherMonth ? 'other-month' : ''} 
                      ${cell.selected ? 'selected' : ''} 
                      ${cell.isToday ? 'today' : ''} 
                      ${cell.disabled ? 'disabled' : ''}"
                    tabindex="${isFocused ? '0' : '-1'}"
                    @click="${() => this._selectDay(cell)}"
                    @keydown="${this._handleGridKeyDown}"
                  >
                    ${cell.day}
                  </div>
                `;
              })}
            </div>
          ` : this.viewMode === 'months' ? html`
            <div class="grid-view-3x4">
              ${monthsShort.map((m, idx) => {
                const isSelected = parsedValue && parsedValue.getMonth() === idx && parsedValue.getFullYear() === this.currentYear;
                const isToday = new Date().getMonth() === idx && new Date().getFullYear() === this.currentYear;
                const isDisabled = this._isMonthDisabled(idx);
                return html`
                  <div 
                    class="grid-cell ${isSelected ? 'selected' : ''} ${isToday ? 'current' : ''} ${isDisabled ? 'disabled' : ''}"
                    @click="${() => this._selectMonth(idx)}"
                  >
                    ${m}
                  </div>
                `;
              })}
            </div>
          ` : html`
            <div class="grid-view-3x4">
              ${yearsGridRange.map(y => {
                const isSelected = parsedValue && parsedValue.getFullYear() === y;
                const isToday = new Date().getFullYear() === y;
                const isDisabled = this._isYearDisabled(y);
                return html`
                  <div 
                    class="grid-cell ${isSelected ? 'selected' : ''} ${isToday ? 'current' : ''} ${isDisabled ? 'disabled' : ''}"
                    @click="${() => this._selectYear(y)}"
                  >
                    ${y}
                  </div>
                `;
              })}
            </div>
          `}
          
          <div class="datepicker-footer">
            <button type="button" class="btn-today" @click="${this._selectToday}">Aujourd'hui</button>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-datepicker')) {
  customElements.define('ui-datepicker', UIDatePicker);
}
